import type { ReactNode } from "react";
import { ArrowRight, FileText, User } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

interface SaludFiscalHeroProps {
  score: number;
  maxScore?: number;
  indicatorsChecked?: number;
  statusLead: string;
  statusAccent: string;
  periodLabel: string;
  description: ReactNode;
  explainHref?: () => void;
  /** Tighter padding / smaller score + ring for dashboard placement. */
  compact?: boolean;
  /** No ring + smaller score + tighter text. Stacks score and status side-by-side. */
  lite?: boolean;
}

/**
 * Dark ink-900 hero panel with mono score + olive italic status + circular SVG progress ring.
 * Pairs with SideStack on the right in the .ef-health grid.
 */
export function SaludFiscalHero({
  score,
  maxScore = 100,
  indicatorsChecked,
  statusLead,
  statusAccent,
  periodLabel,
  description,
  explainHref,
  compact = false,
  lite = false,
}: SaludFiscalHeroProps): JSX.Element {
  const { t } = useTranslation();
  const safeMax = maxScore || 100;
  const roundedScore = Math.max(0, Math.min(safeMax, Math.round(score)));
  const pct = (roundedScore / safeMax) * 100;
  const R = 70;
  const C = 2 * Math.PI * R;
  const offset = pct >= 100 ? 0 : C - (pct / 100) * C;
  const indicators = indicatorsChecked ?? safeMax;

  return (
    <section
      className={cn(
        "ef-hero",
        compact && "ef-hero--compact",
        lite && "ef-hero--lite",
      )}
    >
      <div>
        <div className="eyebrow">{t("estadoFiscal.heroEyebrow", "Salud fiscal")} · {periodLabel}</div>
        <div className="score">
          <span className="v">{roundedScore}</span>
          <span className="max">/ {safeMax}</span>
        </div>
        <div className="status">
          {statusLead} <span className="serif">{statusAccent}</span>
        </div>
        <div className="desc">
          {description}
          {explainHref ? (
            <>
              {" "}
              <a
                onClick={(e) => {
                  e.preventDefault();
                  explainHref();
                }}
              >
                {t("estadoFiscal.heroExplainCta", "Ver estado fiscal explicado")}
                <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </a>
            </>
          ) : null}
        </div>
      </div>
      {lite ? null : (
        <div className="ef-ring" aria-hidden>
          <svg viewBox="0 0 160 160">
            <circle
              className="ring-bg"
              cx="80"
              cy="80"
              r={R}
              strokeWidth="10"
              fill="none"
            />
            <circle
              className="ring-fg"
              cx="80"
              cy="80"
              r={R}
              strokeWidth="10"
              fill="none"
              strokeDasharray={C}
              strokeDashoffset={offset}
            />
          </svg>
          <div className="center">
            <b>
              {indicators} / {safeMax}
            </b>
            <span>{t("estadoFiscal.heroRingLabel", "Indicadores ok")}</span>
          </div>
        </div>
      )}
    </section>
  );
}

interface FacturacionCardProps {
  neto: number;
  iva: number;
  formatCurrency: (n: number) => string;
  invoiceCount?: number;
  rangeLabel?: string;
  onViewInvoices?: () => void;
}

export function FacturacionCard({
  neto,
  iva,
  formatCurrency,
  invoiceCount,
  rangeLabel,
  onViewInvoices,
}: FacturacionCardProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="ef-card">
      <div className="ef-card-head">
        <span className="ico-tile">
          <FileText className="h-3.5 w-3.5" aria-hidden />
        </span>
        {t("estadoFiscal.facturacionDelMes", "Facturación del mes")}
      </div>
      <div className="ef-bill">
        <div className="col">
          <div className="lbl">{t("estadoFiscal.neto", "Neto")}</div>
          <div className="num">
            <span className="v">{formatCurrency(neto)}</span>
            <span className="u">UYU</span>
          </div>
        </div>
        <div className="col iva">
          <div className="lbl">{t("estadoFiscal.iva", "IVA")}</div>
          <div className="num">
            <span className="v">{formatCurrency(iva)}</span>
            <span className="u">UYU</span>
          </div>
        </div>
      </div>
      <div className="ef-bill-foot">
        <span>
          {invoiceCount != null
            ? t("estadoFiscal.facturacionFootCount", "{{count}} facturas", {
                count: invoiceCount,
              })
            : null}
          {invoiceCount != null && rangeLabel ? " · " : null}
          {rangeLabel ?? null}
        </span>
        {onViewInvoices ? (
          <a
            onClick={(e) => {
              e.preventDefault();
              onViewInvoices();
            }}
          >
            {t("estadoFiscal.viewInvoicesCta", "Ver facturas")}
            <ArrowRight className="h-3 w-3" aria-hidden />
          </a>
        ) : null}
      </div>
    </div>
  );
}

interface ContadorCardProps {
  name: string;
  email: string;
  initials: string;
  onViewProfile?: () => void;
  emptyState?: ReactNode;
}

export function ContadorCard({
  name,
  email,
  initials,
  onViewProfile,
  emptyState,
}: ContadorCardProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="ef-card">
      <div className="ef-card-head">
        <span className="ico-tile">
          <User className="h-3.5 w-3.5" aria-hidden />
        </span>
        {t("estadoFiscal.contadorAsignado", "Contador asignado")}
      </div>
      {emptyState ?? (
        <div className="ef-acct">
          <span className="av">{initials}</span>
          <div className="body">
            <div className="name">{name}</div>
            <div className="mail">{email}</div>
          </div>
          {onViewProfile ? (
            <button
              type="button"
              className={cn("fx-btn fx-btn-ghost fx-btn-sm")}
              onClick={onViewProfile}
            >
              {t("estadoFiscal.viewProfile", "Ver perfil")}
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
